import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Keeps the logo digitising fee in step with the cart.
 *
 * Fee quantity = sum of "Logo count" across branded lines (defaults to 1 if branded
 * but count is missing). Independent of cover quantity.
 */

const configElement = document.querySelector('script[data-branding-fee-config]');

/** @type {{ feeVariantId: number, optionName: string, brandedValue: string } | null} */
let config = null;

try {
  config = configElement?.textContent ? JSON.parse(configElement.textContent) : null;
} catch (error) {
  console.warn('[branding-fee] Could not parse config:', error);
}

if (config?.feeVariantId) {
  const settings = config;

  /** @type {WeakSet<Event>} */
  const ownEvents = new WeakSet();

  let syncing = false;
  let resyncRequested = false;
  /** @type {Promise<void> | null} */
  let syncChain = null;

  /** @param {any} item */
  const isFeeLine = (item) => item.variant_id === settings.feeVariantId;

  /** @param {any} item */
  const isBrandedLine = (item) =>
    !isFeeLine(item) &&
    (item.options_with_values ?? []).some(
      (/** @type {any} */ option) => option.name === settings.optionName && option.value === settings.brandedValue
    );

  /** @param {any} item */
  const logoCountForLine = (item) => {
    const raw = item.properties?.['Logo count'] ?? item.properties?.['_Logo count'];
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) return Math.min(4, Math.floor(parsed));
    return 1;
  };

  const getCart = async () => {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Cart fetch failed with ${response.status}`);

    return response.json();
  };

  const cartSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter((id, index, ids) => Boolean(id) && ids.indexOf(id) === index);

  /**
   * @param {number} quantity
   * @param {any[]} feeLines
   */
  const setFeeQuantity = async (quantity, feeLines) => {
    const sections = cartSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();
    const primaryKey = feeLines[0]?.key;
    const adding = quantity > 0 && !primaryKey;

    const event = adding
      ? new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [
            {
              merchandiseId: `gid://shopify/ProductVariant/${settings.feeVariantId}`,
              quantity,
            },
          ],
          promise: deferred.promise,
        })
      : new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines: [{ id: primaryKey ?? String(settings.feeVariantId), quantity }],
          promise: deferred.promise,
        });

    ownEvents.add(event);
    document.dispatchEvent(event);

    try {
      /** @type {Response} */
      let response;

      if (adding) {
        response = await fetch(
          Theme.routes.cart_add_url,
          fetchConfig('json', {
            body: JSON.stringify({
              items: [{ id: settings.feeVariantId, quantity }],
              sections,
            }),
          })
        );
      } else {
        // Collapse any duplicate fee lines into one desired quantity.
        /** @type {Record<string, number>} */
        const updates = {};
        feeLines.forEach((/** @type {any} */ line, index) => {
          updates[line.key] = index === 0 ? quantity : 0;
        });
        if (!feeLines.length) {
          updates[String(settings.feeVariantId)] = quantity;
        }

        response = await fetch(
          Theme.routes.cart_update_url,
          fetchConfig('json', {
            body: JSON.stringify({
              updates,
              sections,
            }),
          })
        );
      }

      if (!response.ok) throw new Error(`Cart update failed with ${response.status}`);

      const cart = await response.json();
      const fullCart = adding ? await getCart() : cart;

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart),
        detail: {
          sections: fullCart.sections ?? cart.sections,
          itemCount: fullCart.item_count ?? cart.item_count,
          source: 'branding-fee',
        },
      });
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  };

  const sync = async () => {
    if (syncing) {
      resyncRequested = true;
      return;
    }

    syncing = true;

    try {
      do {
        resyncRequested = false;

        const cart = await getCart();
        const items = cart.items ?? [];
        const desired = items.filter(isBrandedLine).reduce((total, item) => total + logoCountForLine(item), 0);
        const feeLines = items.filter(isFeeLine);
        const current = feeLines.reduce(
          (/** @type {number} */ total, /** @type {any} */ item) => total + item.quantity,
          0
        );

        if (current === desired && feeLines.length <= 1) continue;

        await setFeeQuantity(desired, feeLines);
      } while (resyncRequested);
    } catch (error) {
      console.warn('[branding-fee] Sync failed:', error);
    } finally {
      syncing = false;
    }
  };

  /** Serialize syncs so overlapping cart events cannot double-add the fee. */
  const enqueueSync = () => {
    syncChain = (syncChain ?? Promise.resolve())
      .then(() => sync())
      .catch(() => {})
      .then(() => {
        /* keep chain alive */
      });
    return syncChain;
  };

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (ownEvents.has(event)) return;

    Promise.resolve(event.promise)
      .catch(() => {})
      .then(() => enqueueSync());
  });

  enqueueSync();
}
